import type {ItemUpdatesWithSourceRepoId, UpdateColumnValueAction} from '../api/columns/contracts/storage'
import useToasts from '../components/toasts/use-toasts'
import {getEnabledFeatures} from '../helpers/feature-flags'
import type {MemexItemModel} from '../models/memex-item-model'
import {ApiError} from '../platform/api-error'
import {
  MAX_BULK_ITEMS,
  useBulkUpdateItemColumnValues,
} from '../state-providers/column-values/use-bulk-update-item-column-value'
import {useHistory} from '../state-providers/history/history'
import {HistoryResources} from '../strings'
import {UpdateValidationError, validateUpdate} from './use-update-item'

export const useBulkUpdateItems = () => {
  const {bulkUpdateColumnValues} = useBulkUpdateItemColumnValues()

  const {addToast} = useToasts()

  const history = useHistory()

  /**
   * @param sourceRepoId If provided, will force labels, assignees, & milestones to be applied within the same repository. This is
   * stricter than the default behavior, which will attempt to search across repositories for matching values.
   * @param description Optional description of the action, used for the history stack. Should be a simple verb phrase.
   */
  const bulkUpdateSingleColumnValue = async (
    items: Array<MemexItemModel>,
    update: UpdateColumnValueAction,
    description = items.length === 1 ? HistoryResources.updateItem : HistoryResources.updateItems,
    sourceRepoId?: number | null,
  ) => {
    const {projects_bulk_edit_improvements} = getEnabledFeatures()
    if (items.length > MAX_BULK_ITEMS) {
      // eslint-disable-next-line @github-ui/dotcom-primer/toast-migration
      addToast({
        message: `Error updating items: Cannot update more than ${MAX_BULK_ITEMS} items at once`,
        type: 'error',
      })
      return
    }

    try {
      // Once projects_bulk_edit_improvements is enabled, validation will be handled in `bulkUpdateColumnValues`
      const isIssueFieldUpdate = 'isIssueField' in update ? update.isIssueField : false
      const itemsNeedValidation = isIssueFieldUpdate || sourceRepoId !== undefined
      let shouldSkipInvalidItems = itemsNeedValidation && projects_bulk_edit_improvements

      if (!projects_bulk_edit_improvements && itemsNeedValidation) {
        validateUpdate(items, update, sourceRepoId ?? null)
        // If we're only updating a single item, always validate it to provide immediate feedback
      } else if (shouldSkipInvalidItems && items.length === 1) {
        validateUpdate(items, update, sourceRepoId ?? null)
        shouldSkipInvalidItems = false
      }

      const itemUpdates = items.map(item => ({itemId: item.id, updates: [update], sourceRepoId}))

      const undo = await bulkUpdateColumnValues(itemUpdates, shouldSkipInvalidItems)

      history?.registerAction({
        revert: undo,
        description,
      })
    } catch (error) {
      if (error instanceof ApiError && !error.message.includes('not valid JSON')) {
        // eslint-disable-next-line @github-ui/dotcom-primer/toast-migration
        addToast({
          message: `Error updating items: ${error.message}`,
          type: 'error',
        })
        throw error // rethrow to handle further up and log for stats
      } else if (error instanceof ApiError && error.status && error.status >= 500) {
        // eslint-disable-next-line @github-ui/dotcom-primer/toast-migration
        addToast({
          message: 'Something went wrong, please try again.',
          type: 'error',
        })
      } else if (error instanceof UpdateValidationError) {
        // eslint-disable-next-line @github-ui/dotcom-primer/toast-migration
        addToast({
          message: error.message,
          type: 'warning',
        })
      } else {
        throw error
      }
    }
  }

  const bulkUpdateMultipleColumnValues = async (
    itemUpdates: Array<ItemUpdatesWithSourceRepoId>,
    description = itemUpdates.length === 1 ? HistoryResources.updateItem : HistoryResources.updateItems,
  ) => {
    const {projects_bulk_edit_improvements} = getEnabledFeatures()

    if (itemUpdates.length > MAX_BULK_ITEMS) {
      // eslint-disable-next-line @github-ui/dotcom-primer/toast-migration
      addToast({
        message: `Error updating items: Cannot update more than ${MAX_BULK_ITEMS} items at once`,
        type: 'error',
      })
      return
    }

    try {
      const undo = await bulkUpdateColumnValues(itemUpdates, projects_bulk_edit_improvements)
      history?.registerAction({
        revert: undo,
        description,
      })
    } catch (error) {
      if (error instanceof ApiError && !error.message.includes('not valid JSON')) {
        // eslint-disable-next-line @github-ui/dotcom-primer/toast-migration
        addToast({
          message: `Error updating items: ${error.message}`,
          type: 'error',
        })
        throw error // rethrow to handle further up and log for stats
      } else if (error instanceof ApiError && error.status && error.status >= 500) {
        // eslint-disable-next-line @github-ui/dotcom-primer/toast-migration
        addToast({
          message: 'Something went wrong, please try again.',
          type: 'error',
        })
      } else if (error instanceof UpdateValidationError) {
        // eslint-disable-next-line @github-ui/dotcom-primer/toast-migration
        addToast({
          message: error.message,
          type: 'warning',
        })
      } else {
        throw error
      }
    }
  }

  return {
    bulkUpdateSingleColumnValue,
    bulkUpdateMultipleColumnValues,
  }
}
